import React from "react";
import { GripVertical, Plus, Pencil, X, Trash } from "lucide-react";
import { Badge, Card, Mono, cn, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@athena/ui";
import { Text } from "@athena/ui";
import {
    DndContext,
    DragOverlay,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent, DragStartEvent, DraggableAttributes, DraggableSyntheticListeners } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CONTENT_TYPE_META } from "./CourseHelpers";

interface SortableDragProps {
    listeners?: DraggableSyntheticListeners;
    attributes?: DraggableAttributes;
    setNodeRef?: (el: HTMLElement | null) => void;
    isDragging?: boolean;
}

interface ModuleCardProps {
    id: string;
    index: number;
    title: string;
    onTitleChange?: (title: string) => void;
    itemCount: number;
    contentIds: string[];
    renderContent: (id: string, dragProps: SortableDragProps) => React.ReactNode;
    onContentReorder?: (fromIndex: number, toIndex: number) => void;
    onAddContent?: () => void;
    addingContent?: boolean;
    onPickType?: (type: "VIDEO" | "TEXT" | "QUIZ") => void;
    onCancelAdd?: () => void;
    onDelete?: () => void;
    listeners?: DraggableSyntheticListeners;
    attributes?: DraggableAttributes;
    setNodeRef?: (el: HTMLElement | null) => void;
    isDragging?: boolean;
    className?: string;
}

const SortableContentItem = ({
    id,
    renderContent,
}: {
    id: string;
    renderContent: (id: string, dragProps: SortableDragProps) => React.ReactNode;
}) => {
    const { listeners, attributes, setNodeRef, isDragging, transform, transition } = useSortable({ id });
    const style = { transform: CSS.Transform.toString(transform), transition };
    return <div style={style}>{renderContent(id, { listeners, attributes, setNodeRef, isDragging })}</div>;
};

export const ModuleCard = ({
    index,
    title,
    onTitleChange,
    itemCount,
    contentIds,
    renderContent,
    onContentReorder,
    onAddContent,
    addingContent,
    onPickType,
    onCancelAdd,
    onDelete,
    listeners,
    attributes,
    setNodeRef,
    isDragging,
    className,
}: ModuleCardProps) => {
    const label = String(index).padStart(2, "0");
    const [
        activeContentId,
        setActiveContentId,
    ] = React.useState<string | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleContentDragStart = (event: DragStartEvent) => {
        setActiveContentId(String(event.active.id));
    };

    const handleContentDragEnd = (event: DragEndEvent) => {
        setActiveContentId(null);
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const fromIndex = contentIds.indexOf(String(active.id));
        const toIndex = contentIds.indexOf(String(over.id));
        if (fromIndex !== -1 && toIndex !== -1) {
            onContentReorder?.(fromIndex, toIndex);
        }
    };

    return (
        <Card
            ref={setNodeRef}
            className={cn("w-full max-w-none overflow-hidden", isDragging && "opacity-50", className)}
        >
            {/* Header */}
            <div className="bg-background flex items-center gap-3 border-b px-4 py-3">
                <button className="cursor-grab touch-none" {...attributes} {...listeners}>
                    <GripVertical className="text-border h-4 w-4 shrink-0" />
                </button>
                <Badge className="bg-secondary/15 text-text-link shrink-0 border-0 font-mono text-xs">{label}</Badge>
                <div className="relative min-w-0 flex-1">
                    <input
                        defaultValue={title}
                        type="text"
                        onChange={(e) => onTitleChange?.(e.target.value)}
                        className="font-display text-text-heading hover:border-border focus:border-secondary focus:bg-card w-full rounded-lg border border-transparent bg-transparent p-1.5 text-lg font-bold tracking-tight transition-colors outline-none"
                    />
                </div>
                <div className="flex shrink-0 items-center gap-4">
                    <Pencil className="text-muted-foreground/40 h-3 w-3" />
                    <Mono size="xs" muted>
                        {itemCount} {itemCount === 1 ? "item" : "items"}
                    </Mono>
                    {onDelete && (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        onClick={onDelete}
                                        disabled={itemCount > 0}
                                        aria-label="Delete module"
                                        className="hover:bg-border rounded-md p-1.5 disabled:cursor-not-allowed disabled:opacity-30"
                                    >
                                        <Trash className="text-muted-foreground h-3.5 w-3.5" />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent className="bg-card text-xs">
                                    {itemCount > 0 ? "Remove all content to delete this module" : "Delete module"}
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                </div>
            </div>

            {/* Rows */}
            {contentIds.length > 0 ? (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleContentDragStart}
                    onDragEnd={handleContentDragEnd}
                >
                    <SortableContext items={contentIds} strategy={verticalListSortingStrategy}>
                        <ul className="divide-border divide-y overflow-hidden rounded-lg">
                            {contentIds.map((contentId) => (
                                <SortableContentItem key={contentId} id={contentId} renderContent={renderContent} />
                            ))}
                        </ul>
                    </SortableContext>
                    <DragOverlay>
                        {activeContentId ? (
                            <div className="rounded-lg opacity-95 shadow-xl">{renderContent(activeContentId, {})}</div>
                        ) : null}
                    </DragOverlay>
                </DndContext>
            ) : (
                <div className="px-5 py-4">
                    <Text muted as="span" className="italic">
                        Empty section — add your first piece of content.
                    </Text>
                </div>
            )}

            {/* Add content */}
            <div className="px-4 py-3">
                {addingContent ? (
                    <div className="flex items-center gap-2">
                        {(
                            [
                                "VIDEO",
                                "TEXT",
                                "QUIZ",
                            ] as const
                        ).map((type) => {
                            const meta = CONTENT_TYPE_META[type];
                            return (
                                <button
                                    key={type}
                                    onClick={() => onPickType?.(type)}
                                    className="border-border hover:bg-muted flex flex-1 flex-col items-center gap-2 rounded-xl border px-3 py-3 transition-colors"
                                >
                                    <span className={meta.color + " rounded-lg p-1 [&>svg]:h-4.5 [&>svg]:w-4.5"}>
                                        {meta.icon}
                                    </span>
                                    <Text size="xs" weight="medium">
                                        {meta.label}
                                    </Text>
                                </button>
                            );
                        })}
                        <button
                            onClick={onCancelAdd}
                            aria-label="Cancel"
                            className="border-border hover:bg-muted flex h-19 w-10 shrink-0 items-center justify-center rounded-lg border transition-colors"
                        >
                            <X className="text-muted-foreground h-4 w-4" />
                        </button>
                    </div>
                ) : (
                    <button
                        onClick={onAddContent}
                        className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 text-sm transition-colors"
                    >
                        <Plus className="h-4 w-4" />
                        Add content
                    </button>
                )}
            </div>
        </Card>
    );
};
